import axios from 'axios';
import { careHeaders, rememberCareSession } from './careApi';
import type {
  CreateMindcareBookingPayload,
  MindcareBarazaSession,
  MindcareBooking,
  MindcareBootstrap,
  MindcareContentRequest,
  MindcareDashboard,
  MindcarePatient,
  MindcarePatientForm,
  MindcarePayment,
  MindcareJournalPrompt,
  MindcareResource,
  MindcareSessionRoom,
  MindcareTherapist,
  MindcareTherapistEarnings,
  MindcareWithdrawalRequest,
  UpdateMindcareAvailabilityPayload,
} from '@mindcare/shared/types';

const base = (backendUrl: string) => `${backendUrl.replace(/\/+$/, '')}/api/mindcare`;

export async function fetchMindcareBootstrap(backendUrl: string): Promise<MindcareBootstrap> {
  const response = await axios.get<{ success: boolean; data: MindcareBootstrap }>(`${base(backendUrl)}/bootstrap`);
  return response.data.data;
}

export async function fetchMindcareTherapists(backendUrl: string): Promise<MindcareTherapist[]> {
  const response = await axios.get<{ success: boolean; therapists: MindcareTherapist[] }>(`${base(backendUrl)}/therapists`);
  return response.data.therapists;
}

export async function fetchMindcareTherapist(backendUrl: string, slug: string): Promise<MindcareTherapist> {
  const response = await axios.get<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}`,
  );
  return response.data.therapist;
}

export async function fetchMindcareTherapistAccount(backendUrl: string, authToken: string): Promise<MindcareTherapist> {
  const response = await axios.get<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/therapist-account`,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  return response.data.therapist;
}

export async function enrollMindcareTherapist(
  backendUrl: string,
  payload: Partial<MindcareTherapist> & { email: string; password?: string },
): Promise<MindcareTherapist> {
  const response = await axios.post<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/therapist-enrollment`,
    payload,
  );
  rememberCareSession(response.data.therapist, 'therapist');
  return response.data.therapist;
}

export async function loginMindcareTherapist(
  backendUrl: string,
  payload: { email: string; password: string },
): Promise<MindcareTherapist> {
  const response = await axios.post<{ success: boolean; therapist: MindcareTherapist }>(`${base(backendUrl)}/therapist-login`, payload);
  rememberCareSession(response.data.therapist, 'therapist');
  return response.data.therapist;
}

export async function updateMindcareAvailability(
  backendUrl: string,
  slug: string,
  payload: UpdateMindcareAvailabilityPayload,
  authToken: string,
): Promise<MindcareTherapist> {
  const response = await axios.put<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/availability`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  return response.data.therapist;
}

export async function createMindcareBooking(
  backendUrl: string,
  payload: CreateMindcareBookingPayload,
): Promise<MindcareBooking> {
  const response = await axios.post<{ success: boolean; booking: MindcareBooking }>(`${base(backendUrl)}/bookings`, payload, { headers: careHeaders() });
  return response.data.booking;
}

export async function initiateMindcareBookingPayment(
  backendUrl: string,
  bookingId: string,
  payload: { provider: 'mpesa' | 'paystack'; phone?: string; callbackUrl?: string },
): Promise<{ payment: MindcarePayment | null; booking: MindcareBooking; room?: MindcareSessionRoom }> {
  const response = await axios.post<{
    success: boolean;
    payment: MindcarePayment | null;
    booking: MindcareBooking;
    room?: MindcareSessionRoom;
  }>(`${base(backendUrl)}/bookings/${encodeURIComponent(bookingId)}/payments`, payload);
  return response.data;
}

export async function confirmMindcareBookingPayment(
  backendUrl: string,
  bookingId: string,
  payload: { provider?: 'mpesa' | 'paystack'; reference?: string },
): Promise<{ payment: MindcarePayment; booking: MindcareBooking; room: MindcareSessionRoom }> {
  const response = await axios.post<{
    success: boolean;
    payment: MindcarePayment;
    booking: MindcareBooking;
    room: MindcareSessionRoom;
  }>(`${base(backendUrl)}/bookings/${encodeURIComponent(bookingId)}/payments/confirm`, payload);
  return response.data;
}

export async function fetchMindcareBookings(backendUrl: string, email?: string): Promise<MindcareBooking[]> {
  const url = new URL(`${base(backendUrl)}/bookings`);
  if (email) url.searchParams.set('email', email);
  const response = await axios.get<{ success: boolean; bookings: MindcareBooking[] }>(url.toString());
  return response.data.bookings;
}

export async function fetchMindcarePatientForms(backendUrl: string, email?: string): Promise<MindcarePatientForm[]> {
  const url = new URL(`${base(backendUrl)}/patient-forms`);
  if (email) url.searchParams.set('email', email);
  const response = await axios.get<{ success: boolean; forms: MindcarePatientForm[] }>(url.toString());
  return response.data.forms;
}

export async function upsertMindcarePatientForm(
  backendUrl: string,
  payload: {
    clientEmail: string;
    clientName?: string;
    formType: 'intake' | 'consent';
    payload: Record<string, any>;
  },
): Promise<MindcarePatientForm> {
  const response = await axios.post<{ success: boolean; form: MindcarePatientForm }>(`${base(backendUrl)}/patient-forms`, payload);
  return response.data.form;
}

export async function fetchMindcareTherapistForms(
  backendUrl: string,
  slug: string,
  authToken: string,
): Promise<MindcarePatientForm[]> {
  const response = await axios.post<{ success: boolean; forms: MindcarePatientForm[] }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/forms`,
    {},
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  return response.data.forms;
}

export async function reviewMindcarePatientForm(
  backendUrl: string,
  slug: string,
  formId: string,
  authToken: string,
  payload: { therapistNotes?: string },
): Promise<MindcarePatientForm> {
  const response = await axios.patch<{ success: boolean; form: MindcarePatientForm }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/forms/${encodeURIComponent(formId)}`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  return response.data.form;
}

export async function fetchMindcareTherapistEarnings(
  backendUrl: string,
  slug: string,
  authToken: string,
): Promise<MindcareTherapistEarnings> {
  const response = await axios.post<{ success: boolean; earnings: MindcareTherapistEarnings }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/earnings`,
    {},
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  return response.data.earnings;
}

export async function createMindcareTherapistWithdrawal(
  backendUrl: string,
  slug: string,
  authToken: string,
  payload: {
    amountCents: number;
    currency?: string;
    method: string;
    destination: string;
    notes?: string;
  },
): Promise<{ withdrawal: MindcareWithdrawalRequest; earnings: MindcareTherapistEarnings }> {
  const response = await axios.post<{
    success: boolean;
    withdrawal: MindcareWithdrawalRequest;
    earnings: MindcareTherapistEarnings;
  }>(`${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/withdrawals`, payload, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return { withdrawal: response.data.withdrawal, earnings: response.data.earnings };
}

export async function createMindcareSessionRoom(backendUrl: string, bookingId: string): Promise<MindcareSessionRoom> {
  const response = await axios.post<{ success: boolean; room: MindcareSessionRoom }>(
    `${base(backendUrl)}/bookings/${encodeURIComponent(bookingId)}/session-room`,
    {}, { headers: careHeaders() },
  );
  return response.data.room;
}

export async function fetchMindcareSessionRoom(backendUrl: string, roomId: string): Promise<MindcareSessionRoom> {
  const response = await axios.get<{ success: boolean; room: MindcareSessionRoom }>(
    `${base(backendUrl)}/session-rooms/${encodeURIComponent(roomId)}`,
    { headers: careHeaders() },
  );
  return response.data.room;
}

export async function endMindcareSessionRoom(
  backendUrl: string,
  roomId: string,
  summary?: string,
): Promise<MindcareSessionRoom> {
  const response = await axios.put<{ success: boolean; room: MindcareSessionRoom }>(
    `${base(backendUrl)}/session-rooms/${encodeURIComponent(roomId)}/end`,
    { summary },
    { headers: careHeaders() },
  );
  return response.data.room;
}

export async function upsertMindcarePatient(
  backendUrl: string,
  payload: {
    name: string;
    email: string;
    phone?: string;
    country?: string;
    emergencyContact?: string;
    clientCategory?: 'student' | 'adult';
    consentAccepted?: boolean;
    password?: string;
  },
): Promise<MindcarePatient> {
  const response = await axios.post<{ success: boolean; patient: MindcarePatient }>(
    `${base(backendUrl)}/patient-portal`,
    payload,
  );
  rememberCareSession(response.data.patient, 'patient');
  return response.data.patient;
}


export async function requestMindcarePatientDataDeletion(
  backendUrl: string,
  payload: { email: string; categories: string[]; details?: string },
): Promise<{ requestId: string; status: string; deletedCategories: string[]; reviewCategories: string[] }> {
  const response = await axios.post<{
    success: boolean;
    result: { requestId: string; status: string; deletedCategories: string[]; reviewCategories: string[] };
  }>(`${base(backendUrl)}/patient-data/deletion-request`, payload);
  return response.data.result;
}

export async function deleteMindcarePatientAccount(
  backendUrl: string,
  payload: { email: string; confirmation: string; reason?: string },
): Promise<{ email: string; deleted: boolean }> {
  const response = await axios.delete<{ success: boolean; result: { email: string; deleted: boolean } }>(
    `${base(backendUrl)}/patient-account`,
    { data: payload },
  );
  return response.data.result;
}

export async function loginMindcarePatient(
  backendUrl: string,
  payload: { email: string; password: string },
): Promise<MindcarePatient> {
  const response = await axios.post<{ success: boolean; patient: MindcarePatient }>(`${base(backendUrl)}/patient-login`, payload);
  rememberCareSession(response.data.patient, 'patient');
  return response.data.patient;
}

export async function requestMindcarePasswordReset(
  backendUrl: string,
  payload: { role: 'patient' | 'therapist'; email: string },
): Promise<{ email: string }> {
  const response = await axios.post<{ success: boolean; email: string }>(`${base(backendUrl)}/password-reset/request`, payload);
  return { email: response.data.email };
}

export async function verifyMindcarePasswordReset(
  backendUrl: string,
  payload: { role: 'patient' | 'therapist'; email: string; otp: string; password: string },
): Promise<{ account: MindcarePatient | MindcareTherapist }> {
  const response = await axios.post<{ success: boolean; account: MindcarePatient | MindcareTherapist }>(
    `${base(backendUrl)}/password-reset/verify`,
    payload,
  );
  return { account: response.data.account };
}

export async function fetchMindcarePatientPortal(backendUrl: string, email?: string): Promise<MindcareDashboard> {
  const url = new URL(`${base(backendUrl)}/patient-portal`);
  if (email) url.searchParams.set('email', email);
  const response = await axios.get<{ success: boolean; portal: MindcareDashboard }>(url.toString());
  return response.data.portal;
}

export async function fetchMindcareBarazas(backendUrl: string): Promise<MindcareBarazaSession[]> {
  const response = await axios.get<{ success: boolean; barazas: MindcareBarazaSession[] }>(`${base(backendUrl)}/barazas`);
  return response.data.barazas;
}

export async function upsertMindcareBaraza(
  backendUrl: string,
  payload: Partial<MindcareBarazaSession> & { title: string; startsAt: string; description: string },
): Promise<MindcareBarazaSession> {
  const response = await axios.put<{ success: boolean; baraza: MindcareBarazaSession }>(
    `${base(backendUrl)}/admin/barazas`,
    payload,
  );
  return response.data.baraza;
}

export async function fetchMindcareAdminTherapists(backendUrl: string): Promise<MindcareTherapist[]> {
  const response = await axios.get<{ success: boolean; therapists: MindcareTherapist[] }>(
    `${base(backendUrl)}/admin/therapists`,
  );
  return response.data.therapists;
}

export async function updateMindcareTherapistApproval(
  backendUrl: string,
  slug: string,
  payload: { approvalStatus: string; adminNotes?: string },
): Promise<MindcareTherapist> {
  const response = await axios.patch<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/admin/therapists/${encodeURIComponent(slug)}/approval`,
    payload,
  );
  return response.data.therapist;
}

export async function updateMindcareTherapistProfile(
  backendUrl: string,
  slug: string,
  authToken: string,
  payload: Partial<MindcareTherapist> & { sessionFeeCents?: number; studentSessionFeeCents?: number; adultSessionFeeCents?: number; pricingTiers?: Record<string, unknown> },
): Promise<MindcareTherapist> {
  const response = await axios.put<{ success: boolean; therapist: MindcareTherapist }>(
    `${base(backendUrl)}/therapists/${encodeURIComponent(slug)}/profile`,
    payload,
    { headers: { Authorization: `Bearer ${authToken}` } },
  );
  rememberCareSession(response.data.therapist, 'therapist');
  return response.data.therapist;
}

export async function fetchMindcareAdminWithdrawals(backendUrl: string, adminToken: string): Promise<MindcareWithdrawalRequest[]> {
  const response = await axios.get<{ success: boolean; withdrawals: MindcareWithdrawalRequest[] }>(
    `${base(backendUrl)}/admin/withdrawals`,
    { headers: { Authorization: `Bearer ${adminToken}` } },
  );
  return response.data.withdrawals;
}

export async function updateMindcareAdminWithdrawal(
  backendUrl: string,
  adminToken: string,
  id: string,
  payload: { status: 'approved' | 'rejected' | 'paid'; payoutReference?: string; adminNotes?: string },
): Promise<MindcareWithdrawalRequest> {
  const response = await axios.patch<{ success: boolean; withdrawal: MindcareWithdrawalRequest }>(
    `${base(backendUrl)}/admin/withdrawals/${encodeURIComponent(id)}`,
    payload,
    { headers: { Authorization: `Bearer ${adminToken}` } },
  );
  return response.data.withdrawal;
}

export async function fetchMindcareResources(backendUrl: string): Promise<MindcareResource[]> {
  const response = await axios.get<{ success: boolean; resources: MindcareResource[] }>(`${base(backendUrl)}/resources`);
  return response.data.resources;
}

export async function upsertMindcareResource(
  backendUrl: string,
  payload: Partial<MindcareResource> & { title: string; description: string },
): Promise<MindcareResource> {
  const response = await axios.put<{ success: boolean; resource: MindcareResource }>(
    `${base(backendUrl)}/admin/resources`,
    payload,
  );
  return response.data.resource;
}

export async function fetchMindcareJournalPrompts(backendUrl: string): Promise<MindcareJournalPrompt[]> {
  const response = await axios.get<{ success: boolean; prompts: MindcareJournalPrompt[] }>(
    `${base(backendUrl)}/journal-prompts`,
  );
  return response.data.prompts;
}

export async function upsertMindcareJournalPrompt(
  backendUrl: string,
  payload: Partial<MindcareJournalPrompt> & { title: string; excerpt: string; prompt: string },
): Promise<MindcareJournalPrompt> {
  const response = await axios.put<{ success: boolean; prompt: MindcareJournalPrompt }>(
    `${base(backendUrl)}/admin/journal-prompts`,
    payload,
  );
  return response.data.prompt;
}

export async function fetchMindcareContentRequests(backendUrl: string): Promise<MindcareContentRequest[]> {
  const response = await axios.get<{ success: boolean; requests: MindcareContentRequest[] }>(
    `${base(backendUrl)}/admin/content-requests`,
  );
  return response.data.requests;
}

export async function createMindcareContentRequest(
  backendUrl: string,
  payload: Omit<Partial<MindcareContentRequest>, 'id' | 'createdAt' | 'updatedAt'> & {
    requesterEmail: string;
    section: 'baraza' | 'resource' | 'journal';
    title: string;
    summary: string;
  },
): Promise<MindcareContentRequest> {
  const response = await axios.post<{ success: boolean; request: MindcareContentRequest }>(
    `${base(backendUrl)}/therapist/content-requests`,
    payload,
  );
  return response.data.request;
}

export async function updateMindcareContentRequest(
  backendUrl: string,
  id: string,
  payload: { status?: string; adminNotes?: string },
): Promise<MindcareContentRequest> {
  const response = await axios.patch<{ success: boolean; request: MindcareContentRequest }>(
    `${base(backendUrl)}/admin/content-requests/${encodeURIComponent(id)}`,
    payload,
  );
  return response.data.request;
}

export async function fetchMindcareDashboard(backendUrl: string, email?: string): Promise<MindcareDashboard> {
  const url = new URL(`${base(backendUrl)}/dashboard`);
  if (email) url.searchParams.set('email', email);
  const response = await axios.get<{ success: boolean; dashboard: MindcareDashboard }>(url.toString());
  return response.data.dashboard;
}

export async function createMindcareJournalEntry(
  backendUrl: string,
  payload: { clientEmail?: string; title: string; mood: string; body: string; tags?: string[] },
) {
  const response = await axios.post(`${base(backendUrl)}/journal`, payload);
  return response.data.entry;
}
